import type { Types } from '@amplitude/unified';

import { getCurrentPageType } from '@/src/lib/clientProfiler';

/**
 * Event property name. Deliberately the same spelling and the same values as
 * the `page_type` key `useGtmRouteChange` pushes onto the dataLayer, so an
 * Amplitude breakdown and a GA4/GTM one group by the same buckets.
 */
export const AMPLITUDE_PAGE_TYPE_PROPERTY = 'page_type';

/**
 * Stamps the current route's declared page type on every event, autocaptured
 * ones included, so findings group by page kind without each call site
 * remembering to pass it.
 *
 * Registered after `initAll` resolves: the timeline applies plugins on a
 * `setTimeout(0)`, so a registration that completes in microtasks still lands
 * before the landing page view is processed.
 *
 * A page view from browser back/forward can carry the previous route's type.
 * The SDK fires that one from its `popstate` listener, before React has
 * committed the new route - so nothing in the app knows the new page type yet,
 * and deriving one from the URL is what the declared type exists to avoid.
 */
export function createPageTypePlugin(): Types.EnrichmentPlugin {
  return {
    name: 'hotpads-page-type',
    type: 'enrichment',
    // eslint-disable-next-line @typescript-eslint/require-await -- the plugin contract is async
    execute: async (event) => {
      const pageType = getCurrentPageType();
      if (!pageType) return event;

      // Ours first: an explicit property on the event wins.
      event.event_properties = { [AMPLITUDE_PAGE_TYPE_PROPERTY]: pageType, ...event.event_properties };
      return event;
    },
  };
}
